/**
 * Shared week status business logic utilities
 * Used by both Candidates and Managers timesheet functionality
 */

/**
 * Determine the status of a week based on shift statuses and pay cycle deadline
 *
 * @param weekData - Array of day data for the week
 * @param payCycle - Pay cycle information from API
 * @returns The week's status
 */
export const determineWeekStatus = (weekData, payCycle) => {
  if (!weekData.length) return null;
  const hasApproved = weekData.some((day) => day.isApproved);
  const hasSubmitted = weekData.some((day) => day.isSubmitted);
  const allSubmitted = weekData.every(
    (day) => day.shifts.length === 0 || day.isSubmitted
  );
  const hasAnyShifts = weekData.some((day) => day.shifts.length > 0);
  const deadlinePassed = payCycle?.hours_submitted_at;

  // If no shifts were submitted and deadline has passed
  if (!hasAnyShifts && deadlinePassed) {
    return "deadline_passed";
  }
  if (hasApproved) return "approved";
  if (allSubmitted && hasSubmitted) return "submitted";
  if (hasSubmitted) return "mixed";
  return "draft";
};

/**
 * Check if a week can be edited/submitted
 * @param weekStatus - Current week status
 * @returns Whether the week can be edited or submitted
 */
export const canEditWeek = (weekStatus) => {
  return weekStatus === "draft" || weekStatus === "mixed";
};

/**
 * Check if a week is in a read-only state (submitted, approved, or deadline passed)
 * Note: This is the inverse of canEditWeek - use whichever reads more naturally
 * @param weekStatus - Current week status
 * @returns Whether the week is read-only
 */
export const isWeekReadOnly = (weekStatus) => {
  return (
    weekStatus === "approved" ||
    weekStatus === "deadline_passed" ||
    weekStatus === "submitted"
  );
};

/**
 * Check if a week has been approved
 * @param weekStatus - Current week status
 * @returns Whether the week is approved
 */
export const isWeekApproved = (weekStatus) => {
  return weekStatus === "approved";
};
